import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import notFoundImg from "../../assets/images/not-found-404.png";

/**
 * Full-screen 404 page shown for any unmatched route (doctor & pharmacy areas).
 * Two-column layout: text + actions on the left, the not-found illustration on the
 * right, with a support/back-to-home footer. Uses the shared not-found image.
 */
const NotFound = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const accent = "#4f5bd5"; // brand-ish blue used for the CTA and links

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        padding: "24px",
        boxSizing: "border-box",
      }}
    >
      {/* Main two-column area */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: "32px",
          maxWidth: "1100px",
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* Left: text + actions */}
        <div
          style={{ flex: "1 1 320px", minWidth: "280px", maxWidth: "460px" }}
        >
          <h1
            style={{
              fontSize: "clamp(80px, 14vw, 150px)",
              lineHeight: 1,
              fontWeight: 800,
              color: "#1f2a44",
              margin: "0 0 4px",
              letterSpacing: "-2px",
            }}
          >
            404
          </h1>
          <h2
            style={{
              fontSize: "clamp(24px, 4vw, 34px)",
              fontWeight: 700,
              color: "#1f2a44",
              margin: "0 0 16px",
            }}
          >
            {t("notFound.title", "Page Not Found")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              color: "#8a90a2",
              lineHeight: 1.6,
              margin: "0 0 28px",
            }}
          >
            {t(
              "notFound.description",
              "Sorry, the page you're looking for doesn't exist or has been moved.",
            )}
          </p>
          <button
            onClick={() => navigate("/")}
            style={{
              padding: "14px 34px",
              borderRadius: "10px",
              border: "none",
              background: accent,
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            {t("notFound.goHome", "Go Home")}
          </button>
        </div>

        {/* Right: illustration */}
        <div
          style={{
            flex: "1 1 360px",
            minWidth: "280px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img
            src={notFoundImg}
            alt={t("notFound.title", "Page not found")}
            style={{ width: "100%", maxWidth: "520px", height: "auto" }}
          />
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: "1px solid #eef0f4",
          paddingTop: "18px",
          marginTop: "12px",
          textAlign: "center",
        }}
      >
        <p style={{ fontSize: "14px", color: "#6b7280", margin: "0 0 10px" }}>
          {t("notFound.needHelp", "Need help?")}{" "}
          <button
            onClick={() => navigate("/doctor-support")}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: accent,
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {t("notFound.contactSupport", "Contact our support team.")}
          </button>
        </p>
        <button
          onClick={() => navigate("/")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "none",
            border: "none",
            color: accent,
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 9.5L12 3l9 6.5" />
            <path d="M5 10v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V10" />
          </svg>
          {t("notFound.backToHome", "Back to Home")}
        </button>
      </div>
    </div>
  );
};

export default NotFound;
